/**
 * SignupForm Component
 * 
 * A reusable signup/registration form component with name, email,
 * password fields, terms agreement, and validation.
 * 
 * @example
 * ```typescript
 * const signupForm = new SignupForm(container, {
 *   onSuccess: (user) => {
 *     console.log('Signed up:', user.email);
 *   },
 *   onSignIn: () => showLoginForm(),
 *   termsUrl: 'https://example.com/terms',
 *   privacyUrl: 'https://example.com/privacy'
 * });
 * await signupForm.initialize();
 * ```
 */

import { IComponent } from '../IComponent';
import { SignupFormOptions, AuthUser } from './AuthTypes';
import { ToasterService } from '@/services/ToasterService';
import { oemConfig } from '@/services/OEMConfig';
import { Logger } from '@/services/Logger';

const logger = Logger.scope('SignupForm');

export class SignupForm implements IComponent {
  private container: HTMLElement;
  private options: SignupFormOptions;

  constructor(container: HTMLElement, options: SignupFormOptions) {
    this.container = container;
    this.options = {
      termsUrl: 'https://www.flexiva.co.uk/terms',
      privacyUrl: 'https://www.flexiva.co.uk/privacy-policy',
      ...options
    };
  }

  async initialize(): Promise<void> {
    ToasterService.init();
    this.render();
    this.bindEvents();
    logger.debug('SignupForm initialized');
  }

  destroy(): void {
    this.container.innerHTML = '';
    logger.debug('SignupForm destroyed');
  }

  /**
   * Render the signup form HTML
   */
  private render(): void {
    const oem = {
      logo: oemConfig.getSplashImage(),
      caption: oemConfig.getBrandName()
    };

    this.container.innerHTML = `
      <div class="signup-form-container w-full max-w-md">
        <div class="text-center mb-8">
          <img src="${oem.logo}" alt="${oem.caption}" class="h-16 mx-auto mb-4">
          <p class="text-base-content/70 mt-2">Join ${oem.caption} today</p>
        </div>

        <form id="signup-form-component" class="space-y-4">
          <!-- Full Name -->
          <div class="form-control">
            <label class="label">
              <span class="label-text">Full Name</span>
            </label>
            <input 
              type="text" 
              id="signup-name-input"
              name="name" 
              autocomplete="name"
              placeholder="Enter your full name" 
              class="input input-bordered w-full" 
              required 
            />
          </div>

          <!-- Email -->
          <div class="form-control">
            <label class="label">
              <span class="label-text">Email</span>
            </label>
            <input 
              type="email" 
              id="signup-email-input"
              name="email" 
              autocomplete="email"
              placeholder="Enter your email" 
              class="input input-bordered w-full" 
              required 
            />
          </div>

          <!-- Password -->
          <div class="form-control">
            <label class="label">
              <span class="label-text">Password</span>
            </label>
            <input 
              type="password" 
              id="signup-password-input"
              name="password" 
              autocomplete="new-password"
              placeholder="Create a password" 
              class="input input-bordered w-full" 
              required 
              minlength="8"
            />
          </div>

          <!-- Confirm Password -->
          <div class="form-control">
            <label class="label">
              <span class="label-text">Confirm Password</span>
            </label>
            <input 
              type="password" 
              id="signup-confirm-password-input"
              name="confirmPassword" 
              autocomplete="new-password"
              placeholder="Confirm your password" 
              class="input input-bordered w-full" 
              required 
              minlength="8"
            />
          </div>

          <!-- Terms -->
          <div class="form-control">
            <label class="label cursor-pointer justify-start gap-3">
              <input type="checkbox" id="signup-terms-checkbox" name="terms" class="checkbox checkbox-primary checkbox-sm" required />
              <span class="label-text text-sm">
                I agree to the <a role="button" id="signup-terms-link" class="link link-primary cursor-pointer">Terms of Service</a> and 
                <a role="button" id="signup-privacy-link" class="link link-primary cursor-pointer">Privacy Policy</a>
              </span>
            </label>
          </div>

          <!-- Submit Button -->
          <button type="submit" id="signup-submit-btn" class="btn btn-success w-full">
            <i class="fas fa-user-plus"></i>
            Create Account
          </button>

          ${this.options.onSignIn ? `
            <!-- Links -->
            <div class="text-center text-sm mt-4">
              <p class="text-base-content/70">
                Already have an account? <a role="button" id="signup-signin-link" class="link link-primary cursor-pointer">Sign in</a>
              </p>
            </div>
          ` : ''}
        </form>
      </div>
    `;
  }

  /**
   * Bind event listeners
   */
  private bindEvents(): void {
    const form = this.container.querySelector('#signup-form-component') as HTMLFormElement;
    const termsLink = this.container.querySelector('#signup-terms-link');
    const privacyLink = this.container.querySelector('#signup-privacy-link');
    const signInLink = this.container.querySelector('#signup-signin-link');

    // Form submission
    form?.addEventListener('submit', (e) => this.handleSubmit(e));

    // Terms link
    termsLink?.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      window.open(this.options.termsUrl, '_blank', 'noopener,noreferrer');
    });

    // Privacy link
    privacyLink?.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      window.open(this.options.privacyUrl, '_blank', 'noopener,noreferrer');
    });

    // Sign in link
    signInLink?.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.options.onSignIn?.();
    });
  }

  /**
   * Handle form submission
   */
  private async handleSubmit(e: Event): Promise<void> {
    e.preventDefault();

    const form = e.target as HTMLFormElement;
    const formData = new FormData(form);
    const name = formData.get('name') as string;
    const email = formData.get('email') as string;
    const password = formData.get('password') as string;
    const confirmPassword = formData.get('confirmPassword') as string;

    // Validate required fields
    if (!name || !email || !password) {
      ToasterService.warning('Please fill in all required fields');
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      ToasterService.warning('Please enter a valid email address');
      return;
    }

    // Validate password match
    if (password !== confirmPassword) {
      ToasterService.warning('Passwords do not match');
      return;
    }

    // Validate password length
    if (password.length < 8) {
      ToasterService.warning('Password must be at least 8 characters');
      return;
    }

    const submitBtn = this.container.querySelector('#signup-submit-btn') as HTMLButtonElement;
    const originalText = submitBtn.innerHTML;
    submitBtn.disabled = true;
    submitBtn.innerHTML = '<span class="loading loading-spinner"></span> Creating account...';

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1500));

      const user: AuthUser = {
        id: 'new-user-' + Date.now(),
        email: email,
        name: name
      };

      ToasterService.success('Account created successfully! Please check your email to verify.');
      this.options.onSuccess(user);
    } catch (error) {
      logger.error('Signup error:', error);
      ToasterService.error('Failed to create account. Please try again.');
      this.options.onError?.(error as Error);
      submitBtn.disabled = false;
      submitBtn.innerHTML = originalText;
    }
  }
}
